import type { ApiResponse, UserInfo } from '@/types';
import { httpServices } from './client';
import { normalizeMemberList } from './normalize';

/** The signed-in user and the members of their organization. */

export interface OrganizationMember {
  id: string;
  name?: string;
  [key: string]: unknown;
}

export interface OrganizationViewRequest {
  is_organization_view: boolean;
}

/** GET /saas/me/info through the SaaS client; null when it fails. */
export const getUserInfo = async (): Promise<UserInfo | null> => {
  try {
    const response = await httpServices.saasService.get<{
      success: boolean;
      data: UserInfo;
    }>('/saas/me/info');
    if (response?.success && response?.data) {
      return response.data;
    }
    return null;
  } catch (e) {
    return null;
  }
};

/** The ids GET /llm/me/info returns (API prompt 17). */
export interface LlmUserIds {
  id: number;
  organization_id: number | null;
}

/**
 * GET /llm/me/info: the signed-in user's id and organization id, compared
 * with a chat run's `user_id`. Null when it fails or carries no id (an API
 * from before prompt 17).
 */
export const getLlmUserIds = async (): Promise<LlmUserIds | null> => {
  try {
    const response = await httpServices.llmService.get<{
      success: boolean;
      data?: { id?: unknown; organization_id?: unknown };
    }>('/me/info');
    const id = Number(response?.data?.id);
    if (!response?.success || !Number.isInteger(id) || id <= 0) return null;
    const raw = response.data?.organization_id;
    const organization = raw == null ? NaN : Number(raw);
    return {
      id,
      organization_id: Number.isInteger(organization) ? organization : null,
    };
  } catch {
    return null;
  }
};

/**
 * POST /llm/organization/members/ids with `{ user_ids }`. A failed request
 * resolves to an empty list.
 */
export const getUsersByIds = async (
  userIds: string[],
): Promise<OrganizationMember[]> => {
  const response = await httpServices.llmService.post<unknown>(
    '/organization/members/ids',
    { user_ids: userIds },
  );
  return normalizeMemberList(response);
};

/** POST /llm/organization/settings/view (admins only). */
export const setOrganizationView = (body: OrganizationViewRequest) =>
  httpServices.llmService.post<ApiResponse<unknown>>(
    '/organization/settings/view',
    body,
  );
